import { useEffect, useMemo, useState } from "react";
import { bool, string, func, arrayOf } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { PriceFormats, MAX_WORK_TYPE_LENGTH } from "@repo/utils";
import { settingsRates } from "@paths";
import { DEFAULT_WT_ROLES, simplifyTextString } from "@utils";
import { useSearchableList } from "@hooks";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  useEstimateEditorStore,
} from "@tools";
import { DifferentSalesRateActionAlert } from "../DifferentSalesRateActionAlert.jsx";
import {
  SearchableListContent,
  MenuButton,
  TextClearInput,
  Checkbox,
} from "@components";
import Grid from "@material-ui/core/Grid";
import { ListItem } from "./components/ListItem";
import { Pencil } from "@assets";

export const TeamMemberSelectDialog = observer(({
  workTypeId,
  defaultValue,
  updateRole,
  removeAutofocus,
  selfRemoveTeamMember,
  autoFocus,
  navigationKey,
  listExistingTeamMembers,
  currentlyAddedTeamMembers,
}) => {
  const { t } = useTranslation();
  const editorStore = useEstimateEditorStore();
  const structure = useEstimateStructure();
  const { hasSellerPrivileges, useProfitability } = useEstimateEditorSettings();

  const { importRoleCostRates, importRoleSalesRates, defaultCostCurrency, defaultSalesCurrency } = editorStore;
  const { priceFormat } = structure.settings;

  const [ roles, setRoles ] = useState([]);
  const [ newRoleData, setNewRoleData ] = useState(null);

  const workTypeResources = listExistingTeamMembers
    ? structure.getWorkTypeById(workTypeId)?.resources?.map(role => ({
      id: role.id,
      roleName: role.name,
      costRate: role.rate,
      salesRate: role.companyUnitCost,
      disabled: currentlyAddedTeamMembers?.includes(role.id)
    })) || []
    : [];

  useEffect(() => {
    setRoles(
      editorStore.workTypes.find(wT => wT.id === workTypeId)?.roles
      || DEFAULT_WT_ROLES.map(roleName => ({ roleName }))
    )
  }, [ editorStore.workTypes ]);

  const hasSalesRates = useMemo(() => Boolean(
    roles?.find(r => r.salesRate)
  ), [ roles ]);

  const rateTextModifier = useMemo(() => (
    structure.settings?.timeModifier === 1 ? "hours" : "days"
  ), [ structure.settings?.timeModifier ]);

  const handleSalesRateImport = () => {
    editorStore.switchRoleSalesRatesImport()
  };

  const handleCostRateImport = () => {
    editorStore.switchRoleCostRatesImport()
  };

  const handleRateAction = (newRole) => {
    updateRole(newRole)
    setNewRoleData(null);
  }

  const handleEmptyName = () => {
    if(newRoleData)
      setNewRoleData(null);
    if(defaultValue)
      return;

    selfRemoveTeamMember();
  }

  const handleNewRole = async ({ name: newName }) => {
    if(!newName && !defaultValue) {
      handleEmptyName();
      return;
    }

    let newRole = roles.find(({ roleName }) => (
      simplifyTextString(roleName) === simplifyTextString(newName)
    ));

    if(newRole)
      setNewRoleData(newRole);
    else
      updateRole({ roleName: newName });
  };

  const selectExistingRole = (role, existingWorkTypeRole=false) => async () => {
    looseFocus();
    setNewRoleData({ ...role, existingWorkTypeRole });
  };

  const {
    containerProps,
    inputProps,
    hadAutofocus,
    hadInteraction,
    looseFocus
  } = useSearchableList({
    errorOnEmpty: true,
    defaultValue,
    autoFocus,
    onAutoFocus: removeAutofocus,
    onBlur: handleEmptyName,
    onChange: handleNewRole
  });

  const availableRoles = useMemo(() => {
    if(!hadInteraction)
      return roles;

    return roles.filter(({ roleName }) => {
      // if(listExistingSummaryRoles && )

      if(roleName === inputProps.value)
        return true;

      return simplifyTextString(roleName).indexOf(
        simplifyTextString(inputProps.value)
      ) >= 0;
    })
  }, [ roles, inputProps.value, hadInteraction ]);

  const availableWorkTypeRoles = useMemo(() => {
    if(!listExistingTeamMembers)
      return [];
    if(!hadInteraction)
      return workTypeResources;

    return workTypeResources?.filter(({ roleName }) => {
      if(roleName === inputProps.value)
        return true;

      return simplifyTextString(roleName).indexOf(
        simplifyTextString(inputProps.value)
      ) >= 0;
    }) || [];
  }, [ workTypeResources, listExistingTeamMembers, currentlyAddedTeamMembers, inputProps.value, hadInteraction ]);

  return (
    <>
      <TextClearInput
        {...inputProps}
        acceptOnClickAway
        confirmOnEnter
        placeholder={
          inputProps.focused
            ? t("views.editor.dialogs.resources.focused")
            : t("views.editor.dialogs.resources.placeholder")
        }
        containerClass="name"
        className="w-max -m-0-5"
        maxLength={MAX_WORK_TYPE_LENGTH}
        preventClickAwayOnElement="#resourceSelector"
        inputClassName="input-name"
        navigationKey={navigationKey}
        noBlurFromEnterKey={hadAutofocus}
      />
      {
        Boolean(newRoleData) &&
        <DifferentSalesRateActionAlert
          onAccept={handleRateAction}
          onCancel={handleEmptyName}
          newElementData={newRoleData}
        />
      }
      <SearchableListContent {...containerProps} id="resourceSelector">
        {
          listExistingTeamMembers &&
          <Grid item container spacing={0} alignItems="center" className="py-1-5 pl-4 pr-2">
            <Grid item xs={4} className="text-xs text-secondary">
              {t("views.editor.summary_table.existing_roles")}
            </Grid>
          </Grid>
        }
        {
          listExistingTeamMembers && availableWorkTypeRoles.map((role) => (
            <ListItem
              key={`role_${role.id}`}
              onClick={selectExistingRole(role, true)}
              role={role}
              americanPriceFormat={priceFormat === PriceFormats.AMERICAN}
              showSalesRates={hasSalesRates}
              disabled={role.disabled}
            />
          ))
        }
        <Grid item container spacing={0} alignItems="center" className="py-1-5 pl-4 pr-2">
          <Grid item xs={4} className="text-xs text-secondary">
            {t("views.editor.summary_table.roles")}
          </Grid>
          <Grid item xs={3}>
            {
              hasSellerPrivileges && useProfitability &&
              <Checkbox
                size="small"
                name="lockOption"
                colorSecondary
                checked={importRoleCostRates}
                onChange={handleCostRateImport}
                label={`${t(`views.editor.summary_table.cost_${rateTextModifier}`)} (${defaultCostCurrency})`}
              />
            }
          </Grid>
          <Grid item xs={3}>
            {
              hasSellerPrivileges && (useProfitability || hasSalesRates) &&
              <Checkbox
                size="small"
                name="lockOption"
                colorSecondary
                checked={importRoleSalesRates}
                onChange={handleSalesRateImport}
                label={`${t(`views.editor.summary_table.rate_${rateTextModifier}`)} (${defaultSalesCurrency})`}
              />
            }
          </Grid>
        </Grid>
        {
          availableRoles.map((role) => (
            <ListItem
              key={`role_${role.id || role.roleName}`}
              onClick={selectExistingRole(role)}
              role={role}
              americanPriceFormat={priceFormat === PriceFormats.AMERICAN}
              showSalesRates={hasSalesRates}
            />
          ))
        }
        <Grid item container justifyContent="space-between" alignItems="center">
          <MenuButton
            icon={<Pencil />}
            color="primary"
            className="w-full h-max py-1-5 pl-4 pr-2"
            to={settingsRates}
            target="_blank"
          >
            {t(`views.editor.summary_table.manage_roles`)}
          </MenuButton>
        </Grid>
      </SearchableListContent>
    </>
  );
});

TeamMemberSelectDialog.propTypes = {
  updateRole: func.isRequired,
  removeAutofocus: func.isRequired,
  selfRemoveTeamMember: func.isRequired,
  defaultValue: string,
  navigationKey: string,
  autoFocus: bool,
  listExistingTeamMembers: bool,
  currentlyAddedTeamMembers: arrayOf(string),
}